import React, { useEffect, useRef, useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import styles from "./EllipsisWithTooltip.module.scss";

type Props = {
  text: string;

  /** BEM: class applied to the visible (ellipsis) text */
  className?: string;

  /** BEM: class applied to tooltip root */
  tooltipClassName?: string;

  /** BEM: class applied to tooltip inner text wrapper */
  tooltipTextClassName?: string;

  placement?:
    | "bottom-start"
    | "bottom"
    | "bottom-end"
    | "top-start"
    | "top"
    | "top-end"
    | "left"
    | "right";

  offsetPx?: number;
  maxWidth?: number | string;
};

const EllipsisWithTooltip: React.FC<Props> = ({
  text,
  className = styles["ellipsis-with-tooltip__text"],
  tooltipClassName = styles["ellipsis-with-tooltip__tooltip"],
  tooltipTextClassName = styles["ellipsis-with-tooltip__tooltip-text"],
  placement = "bottom-start",
  offsetPx = 8,
  maxWidth,
}) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isOverflowed, setIsOverflowed] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const checkOverflow = () => {
    const el = textRef.current;
    if (!el) return;

    const overflow = el.scrollWidth > el.clientWidth;
    setIsOverflowed((prev) => (prev !== overflow ? overflow : prev));
  };

  useEffect(() => {
    checkOverflow();
  }, [text]);
  useEffect(() => {
    const el = textRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;

    const ro = new ResizeObserver(checkOverflow);
    ro.observe(el);

    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const handleScroll = () => setTooltipOpen(false);

    window.addEventListener("scroll", handleScroll, true);

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, []);

  const content = (
    <span
      ref={textRef}
      className={`${className} ${
        isOverflowed ? styles["ellipsis-with-tooltip__text--interactive"] : ""
      }`}
      style={maxWidth ? { maxWidth } : undefined}
    >
      {text}
    </span>
  );

  if (!isOverflowed) return content;

  return (
    <Tooltip
      title={<span className={tooltipTextClassName}>{text}</span>}
      placement={placement}
      open={tooltipOpen}
      onOpen={() => setTooltipOpen(true)}
      onClose={() => setTooltipOpen(false)}
      slotProps={{
        popper: {
          modifiers: [{ name: "offset", options: { offset: [0, offsetPx] } }],
        },
      }}
      componentsProps={{
        tooltip: { className: tooltipClassName },
      }}
    >
      {content}
    </Tooltip>
  );
};

export default EllipsisWithTooltip;
